import React, { ChangeEvent, useState, useEffect, useContext } from "react";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import * as Yup from "yup";
import clsx from "clsx";
import UserContext from "../../../UserContext";
import useInput from "../../../hooks/input/useInput";
import useChangeTracker from "../../../hooks/change-tracker/useChangeTracker";
import ValidationConfiguration from "../../../hooks/change-tracker/ValidationConfiguration";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import TrKeyboardDatePicker from "../../shared/controls/TrKeyboardDatePicker";
import TrTextField from "../../shared/controls/TrTextField";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import SchoolCarrierContainer from "./SchoolCarrier";
import { FinancingRequestBasicInfo } from "../../../models/financing-request/FinancingRequestBasicInfo";
import { SchoolCarrier } from "../../../models/financing-request/SchoolCarrier";
import StepHandler from "../../../models/shared/StepHandler";
import FinancingRequest from "../../../models/financing-request/FinancingRequest";
import SelectItem from "../../../models/shared/SelectItem";
import { VehicleType } from "../../../models/vehicle-type/VehicleType";
import TrackerName from "../../../models/financing-request/FinancingRequestBasicInfoTrackerName";
import FinancingRequestApiClient from "../../../api/FinancingRequestApiClient";
import VehicleTypeApiClient from "../../../api/VehicleTypeApiClient";
import { nameof, generateUniqueId } from "../../../utilities/utilities";
import AuthorizedCarrierList from "./AuthorizedCarrierList";
import { AuthorizedCarrier } from "../../../models/financing-request/AuthorizedCarrier";
import Currency from "../../../models/shared/Currency";

interface Props extends StepHandler {
  financingRequest: FinancingRequest;
  disableDatePick?: boolean;
  updateFinancingRequest: (finRequest: FinancingRequestBasicInfo) => void;
  updateSchoolCarrier: (schoolCarriers: SchoolCarrier[]) => void;
  updateAuthorizedCarrier: (authorizedCarriers: AuthorizedCarrier[]) => void;
  updateStudents: (dateFrom: Date) => void;
}

export default function FinancingRequestBasicInfoStep({
  buttons,
  onNext,
  onStepperSave,
  goToNextStep,
  financingRequest,
  disableDatePick,
  updateFinancingRequest,
  updateSchoolCarrier,
  updateAuthorizedCarrier,
  updateStudents,
}: Props) {
  const classes = useStyles({});
  const { user } = useContext(UserContext);

  const [financingRequestBasicInfo, setfinancingRequestBasicInfo] = useState<
    FinancingRequestBasicInfo
  >({
    classificationMark: financingRequest.classificationMark,
    registerNumber: financingRequest.registerNumber,
    dateFrom: financingRequest.dateFrom,
    dateTo: financingRequest.dateTo,
  } as FinancingRequestBasicInfo);

  const [schoolCarriers, setSchoolCarriers] = useState<SchoolCarrier[]>(
    financingRequest.schoolCarriers
  );

  const [authorizedCarriers, setAuthorizedCarriers] = useState<
    AuthorizedCarrier[]
  >(financingRequest.authorizedCarriers);

  const [vehicleTypeItems, setVehicleTypeItems] = useState<
    SelectItem<VehicleType>[]
  >([]);

  useEffect(() => {
    setfinancingRequestBasicInfo((prev) => ({
      ...prev,
      ...({
        classificationMark: financingRequest.classificationMark,
        registerNumber: financingRequest.registerNumber,
        dateFrom: financingRequest.dateFrom,
        dateTo: financingRequest.dateTo,
      } as FinancingRequestBasicInfo),
    }));

    setSchoolCarriers([...financingRequest.schoolCarriers]);
    setAuthorizedCarriers([...financingRequest.authorizedCarriers]);
  }, [financingRequest]);

  useEffect(() => {
    async function getBasicInfo() {
      const apiClient = new FinancingRequestApiClient();
      const basicInfo = await apiClient.getBasicInfoForNewFinancingRequest();

      setfinancingRequestBasicInfo((prevFin) => ({
        ...prevFin,
        dateTo: basicInfo.validTo == null ? null : new Date(basicInfo.validTo),
      }));
    }

    async function getVehicleTypes() {
      const apiClient = new VehicleTypeApiClient();
      const vehicleTypes = await apiClient.getVehicleTypes();

      setVehicleTypeItems([...vehicleTypeItems, ...vehicleTypes]);
    }

    if (financingRequestBasicInfo.dateTo == null) {
      getBasicInfo();
    }

    if (vehicleTypeItems == null || vehicleTypeItems.length == 0) {
      getVehicleTypes();
    }
  }, []);

  useEffect(() => {
    return updateTrackerDebounceEffect([
      [TrackerName.FinancingRequest, financingRequestBasicInfo],
      [TrackerName.SchoolCarriers, schoolCarriers],
      [TrackerName.AuthorizedCarriers, authorizedCarriers],
    ]);
  }, [financingRequestBasicInfo, schoolCarriers, authorizedCarriers]);

  const { handleTextChange, handleDateChange } = useInput();
  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
    variant: snackbarVariant,
  } = useSnackbar();

  const financingRequestValidationConfig = {
    trackerName: TrackerName.FinancingRequest,
    validationScheme: Yup.object<FinancingRequestBasicInfo>().shape({
      classificationMark: Yup.string()
        .trim()
        .required("Klasifikacijska oznaka je obavezna.")
        .min(1, "Klasifikacijska oznaka ne može biti prazna."),
      registerNumber: Yup.string()
        .trim()
        .required("Urudžbeni broj je obavezan.")
        .min(1, "Urudžbeni broj ne može biti prazan."),
      dateFrom: Yup.date()
        .nullable()
        .required("Datum od je obavezan.")
        .when("dateTo", (dateTo: Date, schema: any) => {
          if (dateTo) {
            const minYear = dateTo.getFullYear() - 1;
            const minDate = new Date(minYear, 8, 1);

            return schema
              .min(
                minDate,
                `"Datum od" mora biti unutar trenutne školske godine`
              )
              .max(
                dateTo,
                `"Datum od" mora biti manji od "Datum do"`
              );
          }
          return schema;
        }),
      dateTo: Yup.date()
        .nullable()
        .required("Datum do je obavezan."),
    }),
  } as ValidationConfiguration;



  const schoolCarriersValidationConfig = {
    trackerName: TrackerName.SchoolCarriers,
    validationScheme: Yup.array().of(
      Yup.object<SchoolCarrier[]>().shape({
        vehicleType: Yup.number().required(),
        registrationPlate: Yup.string().required(),
        dailyMileageTotal: Yup.number()
          .min(0.5, "Kilometri ne mogu biti manji od 0,5.")
          .nullable()
          .required(),
        numberOfStudents: Yup.number().required(),
      })
    ),
  } as ValidationConfiguration;

  const authorizedCarriersValidationConfig = {
    trackerName: TrackerName.AuthorizedCarriers,
    validationScheme: Yup.array().of(
      Yup.object<AuthorizedCarrier[]>().shape({
        name: Yup.string().required(),
        dailyMileage: Yup.number()
          .min(0.5, "Kilometri ne mogu biti manji od 0,5.")
          .nullable()
          .required(),
        dailyPrice: Yup.number()
          .min(0, "Cijena ne može biti manja od 0.")
          .nullable()
          .required(),
        numberOfStudents: Yup.number().required(),
      })
    ),
  } as ValidationConfiguration;

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors,
  } = useChangeTracker(
    [
      financingRequestValidationConfig,
      schoolCarriersValidationConfig,
      authorizedCarriersValidationConfig,
    ],
    false
  );

  const handleSchoolCarrierAdd = () => {
    setSchoolCarriers([
      ...schoolCarriers,
      {
        registrationPlate: "",
        dailyMileageTotal: null,
        numberOfStudents: null,
        vehicleType: null,
      } as SchoolCarrier,
    ]);
  };

  const handleSchoolCarrierDelete = (index: number) => {
    setSchoolCarriers((prevSchoolCarriers) => {
      return prevSchoolCarriers.filter((carrier, i) => {
        return i !== index;
      });
    });
  };

  const handleSchoolCarrierChange = (index: number) => (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type } = e.target;
    setSchoolCarriers((prevSchoolCarriers) =>
      prevSchoolCarriers.map((schoolCarrier, i) => {
        if (i === index)
          return {
            ...schoolCarrier,
            [name]: type === "number" ? +value : value,
          };
        return schoolCarrier;
      })
    );
  };

  const handleSchoolCarrierSelectChange = (index: number) => (
    e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name != null) {
      setSchoolCarriers((prevSchoolCarriers) =>
        prevSchoolCarriers.map((schoolCarrier, i) => {
          if (i === index) return { ...schoolCarrier, [name]: value };
          return schoolCarrier;
        })
      );
    }
  };

  const handleAuthorizedCarrierAdd = () => {
    setAuthorizedCarriers([
      ...authorizedCarriers,
      {
        id: generateUniqueId(),
        name: "",
        dailyMileage: null,
        dailyPrice: null,
        numberOfStudents: null,
      } as AuthorizedCarrier,
    ]);
  };

  const handleAuthorizedCarrierDelete = (index: number) => {
    setAuthorizedCarriers((prevAuthorizedCarriers) => {
      return prevAuthorizedCarriers.filter((carrier, i) => {
        return i !== index;
      });
    });
  };

  const handleAuthorizedCarrierChange = (index: number) => (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type } = e.target;
    setAuthorizedCarriers((prevAuthorizedCarriers) =>
      prevAuthorizedCarriers.map((authorizedCarrier, i) => {
        if (i === index)
          return {
            ...authorizedCarrier,
            [name]: type === "number" ? +value : value,
          };
        return authorizedCarrier;
      })
    );
  };

  const handleValidations = () => {
    if (!validationResult.isValid(false)) {
      showErrors();

      const dateToMaxValidationMessage = validationResult
        .getTrackerMessages(TrackerName.FinancingRequest, false)
        .filter(
          (m) =>
            m.objectPath === nameof<FinancingRequestBasicInfo>("dateFrom") &&
            m.validationType === "max"
        );

      const dateFromMinValidationMessage = validationResult
        .getTrackerMessages(TrackerName.FinancingRequest, false)
        .filter(
          (m) =>
            m.objectPath === nameof<FinancingRequestBasicInfo>("dateFrom") &&
            m.validationType === "min"
        );

      const schoolCarrierValidationMessage = validationResult
        .getTrackerMessages(TrackerName.SchoolCarriers, false)
        .filter(
          (m) =>
            m.propertyName === nameof<SchoolCarrier>("dailyMileageTotal") &&
            m.validationType === "min"
        );

      const authorizedCarrierValidationMessage = validationResult
        .getTrackerMessages(TrackerName.AuthorizedCarriers, false)
        .filter(
          (m) =>
            (m.propertyName === nameof<AuthorizedCarrier>("dailyMileage") &&
              m.validationType === "min") ||
            (m.propertyName === nameof<AuthorizedCarrier>("dailyPrice") &&
              m.validationType === "min")
        );

      let message = "";
      if (dateToMaxValidationMessage.length === 1) {
        message = dateToMaxValidationMessage[0].message;
      } else if (dateFromMinValidationMessage.length === 1) {
        message = dateFromMinValidationMessage[0].message;
      } else if (schoolCarrierValidationMessage.length === 1) {
        message = schoolCarrierValidationMessage[0].message;
      } else if (authorizedCarrierValidationMessage.length > 0) {
        message = authorizedCarrierValidationMessage[0].message;
      } else {
        message =
          "Ne možete spremiti podatke dok ne ispunite sva obavezna polja. Molimo provjerite vrijednosti u crveno označenim poljima.";
      }

      openSnackbar(message, "error");
      return false;
    }
    return true;
  };


  const handleNextStep = () => {
    handleValidations() && goToNextStep && goToNextStep();

    updateFinancingRequest(financingRequestBasicInfo);
    updateSchoolCarrier(schoolCarriers);
    updateAuthorizedCarrier(authorizedCarriers);
    updateStudents(financingRequestBasicInfo.dateFrom!);
  };

  const handleUpdateState = () => {
    if (validationResult.isValid(false)) {
      updateFinancingRequest(financingRequestBasicInfo);
      updateSchoolCarrier(schoolCarriers);
      updateAuthorizedCarrier(authorizedCarriers);
      updateStudents(financingRequestBasicInfo.dateFrom!);
    } else {
      return handleValidations();
    }
  };

  onNext && onNext(handleNextStep);

  onStepperSave && onStepperSave(handleUpdateState);



  return (
    <div className={classes.root}>
      <ExpansionPanel>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            className={clsx(
              classes.heading,
              !validationResult.isTrackerValid(TrackerName.FinancingRequest) &&
              classes.error
            )}
          >
            Unos osnovnih podataka
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.basicInfoDetails}>
          <Grid container item spacing={5} md={12}>
            <Grid item xl={3} lg={3} md={6} sm={12} xs={12}>
              <TrKeyboardDatePicker
                required
                format="dd.MM.yyyy."
                label="Datum od"
                disabled={disableDatePick}
                value={financingRequestBasicInfo.dateFrom}
                helperText="Datum od kada vrijedi zahtjev"
                className={classes.textField}
                onChange={(date) => {
                  handleDateChange(
                    nameof<typeof financingRequestBasicInfo>("dateFrom"),
                    date,
                    setfinancingRequestBasicInfo
                  );
                  updateFinancingRequest(({
                    ...financingRequestBasicInfo,
                    dateFrom: date,
                  }));
                }}
                error={
                  !validationResult.isPropertyValid(
                    TrackerName.FinancingRequest,
                    nameof<typeof financingRequestBasicInfo>("dateFrom")
                  )
                }
              />
            </Grid>
            <Grid item xl={3} lg={3} md={6} sm={12} xs={12}>
              <TrKeyboardDatePicker
                format="d. M. yyyy."
                label="Datum do"
                value={financingRequestBasicInfo.dateTo}
                helperText="Kraj nastavne godine"
                className={classes.textField}
                disabled
                onChange={(date) => {
                  updateFinancingRequest(({
                    ...financingRequestBasicInfo,
                    dateTo: date,
                  }));
                }}
                error={
                  !validationResult.isPropertyValid(
                    TrackerName.FinancingRequest,
                    nameof<typeof financingRequestBasicInfo>("dateTo")
                  )
                }
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            spacing={5}
            lg={12}
            className={classes.basicInfoScndRow}
          >
            <Grid item xl={3} lg={3} md={6} sm={12} xs={12}>
              <TrTextField
                required
                label="Klasifikacijska oznaka"
                name={nameof<typeof financingRequestBasicInfo>("classificationMark")}
                value={financingRequestBasicInfo.classificationMark}
                className={classes.textField}
                onChange={(e) => {
                  handleTextChange(e, setfinancingRequestBasicInfo);
                }}
                onBlur={(e) => {
                  updateFinancingRequest({
                    ...financingRequestBasicInfo,
                    classificationMark: e.target.value,
                  });
                }}
                error={
                  !validationResult.isPropertyValid(
                    TrackerName.FinancingRequest,
                    nameof<typeof financingRequestBasicInfo>("classificationMark")
                  )
                }
              />
            </Grid>
            <Grid item xl={3} lg={3} md={6} sm={12} xs={12}>
              <TrTextField
                required
                label="Urudžbeni broj"
                name={nameof<typeof financingRequestBasicInfo>("registerNumber")}
                value={financingRequestBasicInfo.registerNumber}
                className={classes.textField}
                onChange={(e) => {
                  handleTextChange(e, setfinancingRequestBasicInfo);
                }}
                onBlur={(e) => {
                  updateFinancingRequest({
                    ...financingRequestBasicInfo,
                    registerNumber: e.target.value,
                  });
                }}
                error={
                  !validationResult.isPropertyValid(
                    TrackerName.FinancingRequest,
                    nameof<typeof financingRequestBasicInfo>("registerNumber")
                  )
                }
              />
            </Grid>
          </Grid>
        </ExpansionPanelDetails>
      </ExpansionPanel>
      <ExpansionPanel>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            className={clsx(
              classes.heading,
              !validationResult.isTrackerValid(TrackerName.SchoolCarriers) &&
              classes.error
            )}
          >
            Unos školskih prijevoznika
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <SchoolCarrierContainer
            schoolCarriers={schoolCarriers}
            vehicleTypeItems={vehicleTypeItems}
            onChange={handleSchoolCarrierChange}
            onSelectChange={handleSchoolCarrierSelectChange}
            onAdd={handleSchoolCarrierAdd}
            onDelete={handleSchoolCarrierDelete}
            validationResult={validationResult}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>
      <ExpansionPanel>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            className={clsx(
              classes.heading,
              !validationResult.isTrackerValid(
                TrackerName.AuthorizedCarriers
              ) && classes.error
            )}
          >
            Unos ovlaštenih prijevoznika
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <AuthorizedCarrierList
            authorizedCarriers={authorizedCarriers}
            onChange={handleAuthorizedCarrierChange}
            onAdd={handleAuthorizedCarrierAdd}
            onDelete={handleAuthorizedCarrierDelete}
            validationResult={validationResult}
            currency={financingRequest.currency}
          />
        </ExpansionPanelDetails>
      </ExpansionPanel>
      {buttons}
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    heading: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 500,
    },
    textField: {
      width: "100%",
      maxWidth: "100%",
    },
    basicInfoDetails: {
      display: "block",
    },
    basicInfoScndRow: {
      marginTop: "20px",
    },
    error: {
      color: theme.palette.error.main,
    },
  })
);
